/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { AlertTriangle, ChevronLeft, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface DowngradeConfirmationProps {
  currentPlan: any;
  newPlan: any;
  onBack: () => void;
  onConfirm: () => void;
  onContactSales: () => void;
  selectedMailboxes?: number[]; // Add this prop
}

export function DowngradeConfirmation({
  currentPlan,
  newPlan,
  onBack,
  onConfirm,
  onContactSales,
  selectedMailboxes = [], // Default to empty array
}: DowngradeConfirmationProps) {
  const [confirmationChecks, setConfirmationChecks] = useState({
    loseFeatures: false,
    serviceImpact: false,
    noRefund: false,
  });

  const isCustomPlan = currentPlan.name === "Custom";
  const pricingUnit = currentPlan.type === "b2c" ? "domain" : "mailbox";
  const allChecked = Object.values(confirmationChecks).every(Boolean);

  const handleCheckChange = (key: keyof typeof confirmationChecks) => {
    setConfirmationChecks({
      ...confirmationChecks,
      [key]: !confirmationChecks[key],
    });
  };

  const calculateRemainingValue = () => {
    // If on annual plan, calculate remaining value
    if (currentPlan.billingCycle?.toLowerCase() === "annual") {
      // Assuming we're 4 months into a 12-month plan as an example
      const monthsRemaining = 8;
      const remainingValue = currentPlan.price * monthsRemaining;
      return remainingValue;
    }
    return 0;
  };

  // Calculate total cost before and after downgrade
  const calculateTotalCost = () => {
    if (currentPlan.type === "b2b" && currentPlan.mailboxes) {
      const totalMailboxes = currentPlan.mailboxes.length;
      const downgradingMailboxes = selectedMailboxes.length;
      const remainingMailboxes = totalMailboxes - downgradingMailboxes;

      const currentCost = totalMailboxes * currentPlan.price;
      const newCost =
        downgradingMailboxes * newPlan.price +
        remainingMailboxes * currentPlan.price;

      return {
        before: currentCost,
        after: newCost,
        savings: currentCost - newCost,
      };
    }

    return {
      before: currentPlan.price,
      after: newPlan.price,
      savings: currentPlan.price - newPlan.price,
    };
  };

  const costCalculation = calculateTotalCost();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Final Confirmation</h2>
        <p className="text-muted-foreground text-sm md:text-base">
          Please review and confirm your plan downgrade.
        </p>
      </div>

      <Card>
        <CardHeader className="p-4 pb-0">
          <CardTitle>Downgrade Summary</CardTitle>
          <CardDescription>
            You are downgrading from {currentPlan.name} to {newPlan.name}
            {currentPlan.type === "b2b" &&
              currentPlan.mailboxes &&
              ` for ${selectedMailboxes.length} of ${currentPlan.mailboxes.length} mailboxes`}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-4">
            {currentPlan.type === "b2b" && currentPlan.mailboxes && (
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="font-medium">Mailboxes to Downgrade</span>
                <span className="text-right">
                  {selectedMailboxes.length} of {currentPlan.mailboxes.length}
                </span>
              </div>
            )}
            <div className="flex justify-between items-center pb-2 border-b">
              <span className="font-medium">Current Plan</span>
              <span className="text-right">
                {currentPlan.name} (${currentPlan.price}/
                {currentPlan.billingCycle?.toLowerCase() === "monthly" ? "mo" : "yr"} per{" "}
                {pricingUnit})
              </span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b">
              <span className="font-medium">New Plan</span>
              <span className="text-right">
                {newPlan.name} (${newPlan.price}/
                {currentPlan.billingCycle?.toLowerCase() === "monthly" ? "mo" : "yr"} per{" "}
                {pricingUnit})
              </span>
            </div>
            <div
              className={cn(
                "flex justify-between items-center pb-2",
                currentPlan.billingCycle?.toLowerCase() === "annual" && "border-b"
              )}
            >
              <span className="font-medium">Effective Date</span>
              <span className="text-right">
                {format(new Date(currentPlan.nextBillingDate), "MMM d, yyyy")}
              </span>
            </div>
            {currentPlan.billingCycle?.toLowerCase() === "annual" && (
              <div className="flex justify-between items-center">
                <span className="font-medium">Prorated Credit</span>
                <span>Will be calculated and applied to your new plan</span>
              </div>
            )}

            {/* Add total cost calculation */}
          </div>
        </CardContent>
      </Card>

      {isCustomPlan && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Custom Plan Downgrade</AlertTitle>
          <AlertDescription>
            <p>
              You are currently on a Custom plan with tailored features.
              Downgrading to a standard plan requires coordination with our
              sales team.
            </p>
            <Button variant="outline" className="mt-2" onClick={onContactSales}>
              Contact Sales
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {currentPlan.billingCycle?.toLowerCase() === "annual" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <h3 className="font-medium text-blue-800 mb-2">Annual Plan Credit</h3>
          <p className="text-sm text-blue-700">
            You're currently on an annual plan. If you downgrade, you'll receive
            a prorated credit of approximately ${calculateRemainingValue()} for
            the remaining period.
          </p>
          <div className="mt-3 text-xs text-blue-600 flex items-center">
            <Info className="h-4 w-4 mr-1.5" />
            <span>This credit will be applied to your new plan.</span>
          </div>
        </div>
      )}

      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Please Confirm Your Understanding</AlertTitle>
        <AlertDescription className="space-y-3 mt-2">
          <div className="flex items-start space-x-2">
            <Checkbox
              id="loseFeatures"
              checked={confirmationChecks.loseFeatures}
              onCheckedChange={() => handleCheckChange("loseFeatures")}
              className="mt-1 border-destructive bg-white data-[state=checked]:bg-destructive"
            />
            <label
              htmlFor="loseFeatures"
              className="text-sm leading-tight cursor-pointer"
            >
              I understand that I will lose access to features including{" "}
              {currentPlan.features.warmupEmails -
                newPlan.features.warmupEmails}{" "}
              fewer daily warmup emails,{" "}
              {currentPlan.features.healthTests - newPlan.features.healthTests}{" "}
              fewer Email health tests per month
              {currentPlan.features.api && !newPlan.features.api
                ? "API access"
                : ""}{" "}
              {currentPlan.features.support.includes("Dedicated Manager") &&
              !newPlan.features.support.includes("Dedicated Manager")
                ? "dedicated account manager"
                : ""}
              .
            </label>
          </div>

          <div className="flex items-start space-x-2">
            <Checkbox
              id="serviceImpact"
              checked={confirmationChecks.serviceImpact}
              onCheckedChange={() => handleCheckChange("serviceImpact")}
              className="mt-1 border-destructive bg-white data-[state=checked]:bg-destructive"
            />
            <label
              htmlFor="serviceImpact"
              className="text-sm leading-tight cursor-pointer"
            >
              I understand that this downgrade may impact my email
              deliverability and campaign performance based on my current usage
              patterns.
            </label>
          </div>

          <div className="flex items-start space-x-2">
            <Checkbox
              id="noRefund"
              checked={confirmationChecks.noRefund}
              onCheckedChange={() => handleCheckChange("noRefund")}
              className="mt-1 border-destructive bg-white data-[state=checked]:bg-destructive"
            />
            <label
              htmlFor="noRefund"
              className="text-sm leading-tight cursor-pointer"
            >
              I understand that this downgrade will take effect on my next
              billing date (
              {format(new Date(currentPlan.nextBillingDate), "MMM d, yyyy")})
              and no refunds will be issued for the current billing period.
            </label>
          </div>
        </AlertDescription>
      </Alert>

      <Alert variant="info">
        <Info className="h-4 w-4" />
        <AlertTitle>Still not sure?</AlertTitle>
        <AlertDescription>
          <p>
            You can cancel this downgrade at any time before{" "}
            {format(new Date(currentPlan.nextBillingDate), "MMM d, yyyy")} from
            your dashboard.
          </p>
          <p className="mt-2">
            Need help deciding? Our team is here to assist you.
          </p>
          <Button
            variant="outline"
            className="mt-2 bg-white border-blue-300 text-blue-700 hover:bg-blue-100"
            onClick={() =>
              window.open(
                "https://calendly.com/inboxdoctor-sales/inboxdoctor-custom-plan",
                "_blank"
              )
            }
          >
            Talk to Sales
          </Button>
        </AlertDescription>
      </Alert>

      {/* <div className="border rounded-lg p-4 mt-4">
        <h3 className="font-medium mb-2">Schedule a Consultation</h3>
        <p className="text-sm text-muted-foreground">
          Not sure about downgrading? Schedule a free 15-minute consultation with our team to discuss your needs.
        </p>
        <Button variant="outline" className="mt-3" onClick={onContactSales}>
          Schedule Consultation
        </Button>
      </div> */}

      <div className="pt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between sm:items-center">
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={onConfirm}
          disabled={!allChecked}
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          Confirm Downgrade
        </Button>
      </div>
    </div>
  );
}
